import { useEffect, useState } from "react"
import styled from "styled-components"

import { spacing } from "@minuthq/meatball-ui-react"

import { Container } from "src/ui/Container/Container"
import { DatePickerDropdown } from "src/ui/DateRangePicker/DatePickerDropdown"
import { DateRangePickerContainerProps } from "src/ui/DateRangePicker/DateRangePickerContainer"
import { getInitialTime } from "src/ui/MDateTimePicker/dateTimePickerUtils"
import {
  MTimePickerInput,
  TMTimePickerInputProps,
} from "src/ui/MTimePickerInput/MTimePickerInput"
import { TMTimePickerInputValue } from "src/ui/MTimePickerInput/timePickerUtils"

export type TDateTimePickerProps = {
  initialDate?: Date | null
  datePickerProps?: Partial<DateRangePickerContainerProps>
  datePickerLabel?: string
  timePickerLabel?: string
  timePickerInputProps?: Partial<TMTimePickerInputProps>
  required?: boolean
  onChange: (date: Date | null) => void
  disabled?: boolean
}

export function MDateTimePicker({
  initialDate,
  datePickerProps,
  datePickerLabel,
  timePickerLabel,
  timePickerInputProps,
  required,
  onChange,
  disabled,
}: TDateTimePickerProps) {
  const [date, setDate] = useState<Date | null>(initialDate ?? null)
  const [time, setTime] = useState<TMTimePickerInputValue | null>(
    getInitialTime(initialDate)
  )

  // Combine date (if both are set) and update callers whenever it changes
  useEffect(() => {
    if (!date || !time) {
      onChange(null) // make sure to reset the callers state in case the date time was previously set
      return
    }

    const newDate = new Date(date)
    const [hours, minutes] = time.split(":")

    newDate.setHours(Number(hours))
    newDate.setMinutes(Number(minutes))

    onChange(newDate)
    return
  }, [date, time]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Container>
      <Box>
        <DatePickerDropdown
          label={datePickerLabel}
          date={date}
          onChange={setDate}
          dateRangePickerProps={{
            futureDatesSelectable: true,
            ...datePickerProps,
          }}
          required={required}
          disabled={disabled}
        />

        <MTimePickerInput
          initialValue={time}
          label={timePickerLabel}
          onChange={setTime}
          required={required}
          disabled={disabled}
          {...timePickerInputProps}
        />
      </Box>
    </Container>
  )
}

const Box = styled.div`
  display: grid;
  gap: ${spacing.M};

  @container (width > 500px) {
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  }

  @container (width > 700px) {
    grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  }
`
